import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useAuth } from "./use-auth";
import { useQuery } from "@tanstack/react-query";

type Customer = {
  id: string;
  username: string;
  email: string;
  role: string;
};

type CustomerContextType = {
  activeCustomerId: string | null;
  setActiveCustomerId: (id: string | null) => void;
  customers: Customer[];
  activeCustomer: Customer | null;
  isLoading: boolean;
};

const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

export function CustomerProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  // Use localStorage to persist the selected customer ID across sessions
  const [activeCustomerId, setCustomerId] = useState<string | null>(() => {
    const val = localStorage.getItem("activeCustomerId");
    if (val === "null" || val === "undefined" || val === "") return null;
    return val;
  });

  const { data: customers = [], isLoading } = useQuery<Customer[]>({
    queryKey: ["/api/facilities"],
    enabled: !!user,
  });

  // Whenever the active ID changes, sync to local storage
  const setActiveCustomerId = (id: string | null) => {
    const cleanId = (id === "null" || id === "undefined" || id === "") ? null : id;
    setCustomerId(cleanId);
    if (cleanId) {
      localStorage.setItem("activeCustomerId", cleanId);
    } else {
      localStorage.removeItem("activeCustomerId");
    }
  };

  // Auto-select the first facility for non-admins if none is selected
  useEffect(() => {
    const list = Array.isArray(customers) ? customers : [];
    if (!activeCustomerId && list.length > 0 && user && user.role !== "Admin" && user.role !== "super_admin") {
      setActiveCustomerId(list[0].id.toString());
    }
  }, [customers, activeCustomerId, user]);

  // Clean up activeCustomerId if it is invalid/not found in current list
  useEffect(() => {
    const list = Array.isArray(customers) ? customers : [];
    if (!isLoading && activeCustomerId && list.length > 0) {
      const exists = list.some((c) => c.id === activeCustomerId);
      if (!exists) {
        setActiveCustomerId(null);
      }
    }
  }, [customers, isLoading, activeCustomerId]);

  const activeCustomer = (Array.isArray(customers) ? customers : []).find((c) => c.id === activeCustomerId) || null;

  return (
    <CustomerContext.Provider
      value={{
        activeCustomerId,
        setActiveCustomerId,
        customers,
        activeCustomer,
        isLoading,
      }}
    >
      {children}
    </CustomerContext.Provider>
  );
}

export function useCustomer() {
  const context = useContext(CustomerContext);
  if (context === undefined) {
    throw new Error("useCustomer must be used within a CustomerProvider");
  }
  return context;
}
